// Comet ball skin rendering helpers — shared by GameCanvas and SkinCard preview.
//
// Visual target (matches the user-provided "Comet" reference art):
// a near-pure-white nucleus with cyan halo, leaving a long luminous tail
// that sweeps cyan → violet → magenta → fading white, peppered with
// dense sparkle particles. Layers use additive (`lighter`) compositing
// so overlapping gradients build up like real light rays.

export const DEFAULT_SKIN = {
  id: "cosmic-comet",
  name: "Cosmic Comet",
  tier: "free",
  core: "#e0f7ff",         // near-white cyan core
  flame: ["#3aa8ff", "#9b51ff", "#ff3df0"],  // cyan → violet → magenta
  sparkles: "#ffffff",
};

// Pseudo-random (deterministic-per-frame) helper for sparkle placement.
// Seeded by a phase value — pass `performance.now() * 0.001` to get a
// flowing sparkle pattern that doesn't strobe.
function _rand(seed) {
  const s = Math.sin(seed * 9301.0 + 49297.0) * 233280.0;
  return s - Math.floor(s);
}

// Draw a comet ball into a canvas 2d context.
// Core (head) is at (x,y). Tail points opposite to velocity angle.
export function drawComet(ctx, skin, x, y, radius, opts = {}) {
  const { angle = Math.PI / 2, trail = null, glowBoost = 1 } = opts;
  const flame = skin.flame && skin.flame.length >= 2 ? skin.flame : ["#ff4fb3", "#ff007f"];
  const core = skin.core || "#ffe1f0";
  const primary = flame[0];
  const midHex = flame[1];
  const deepHex = flame[flame.length - 1];

  const tailAngle = angle + Math.PI;

  // --- Layered atmospheric tail (additive passes) -------------------------
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  ctx.translate(x, y);
  ctx.rotate(tailAngle);

  const tailLen = radius * 9;
  const tailHalf = radius * 2.2;

  // Pass 1 — widest, softest outer halo of the tail
  {
    const g = ctx.createLinearGradient(0, 0, tailLen, 0);
    g.addColorStop(0, hexWithAlpha(primary, 0.55));
    g.addColorStop(0.3, hexWithAlpha(midHex, 0.28));
    g.addColorStop(1, hexWithAlpha(deepHex, 0));
    ctx.fillStyle = g;
    ctx.shadowColor = primary;
    ctx.shadowBlur = 30 * glowBoost;
    ctx.beginPath();
    ctx.moveTo(radius * 0.9, -tailHalf);
    ctx.quadraticCurveTo(tailLen * 0.45, -tailHalf * 1.8, tailLen, 0);
    ctx.quadraticCurveTo(tailLen * 0.45, tailHalf * 1.8, radius * 0.9, tailHalf);
    ctx.closePath();
    ctx.fill();
  }

  // Pass 2 — mid-width core tail, brighter along the spine
  {
    const g = ctx.createLinearGradient(0, 0, tailLen * 0.85, 0);
    g.addColorStop(0, hexWithAlpha("#ffffff", 0.85));
    g.addColorStop(0.15, hexWithAlpha(core, 0.75));
    g.addColorStop(0.45, hexWithAlpha(primary, 0.55));
    g.addColorStop(1, hexWithAlpha(midHex, 0));
    ctx.fillStyle = g;
    ctx.shadowColor = primary;
    ctx.shadowBlur = 18 * glowBoost;
    ctx.beginPath();
    ctx.moveTo(radius * 0.7, -tailHalf * 0.55);
    ctx.quadraticCurveTo(tailLen * 0.5, -tailHalf * 0.9, tailLen * 0.9, 0);
    ctx.quadraticCurveTo(tailLen * 0.5, tailHalf * 0.9, radius * 0.7, tailHalf * 0.55);
    ctx.closePath();
    ctx.fill();
  }

  // Pass 3 — three thin bright streaks ("striations") across the tail
  ctx.shadowBlur = 0;
  for (let i = -1; i <= 1; i++) {
    const offset = i * tailHalf * 0.35;
    const g = ctx.createLinearGradient(0, 0, tailLen, 0);
    g.addColorStop(0, hexWithAlpha("#ffffff", 0.75));
    g.addColorStop(0.2, hexWithAlpha(core, 0.55));
    g.addColorStop(0.7, hexWithAlpha(primary, 0.2));
    g.addColorStop(1, hexWithAlpha(primary, 0));
    ctx.strokeStyle = g;
    ctx.lineWidth = Math.max(1, radius * (i === 0 ? 0.22 : 0.14));
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(radius * 0.6, offset * 0.2);
    ctx.quadraticCurveTo(tailLen * 0.55, offset, tailLen * 0.95, offset);
    ctx.stroke();
  }

  // Pass 4 — sparkle particles scattered along the tail (signature
  // element of the reference Comet art). Particle positions are seeded
  // by `performance.now()` so the pattern flows but doesn't strobe.
  // We draw inside the same rotated frame so sparkles follow the tail.
  const phase = (typeof performance !== "undefined" ? performance.now() : 0) * 0.0006;
  const sparkleCount = Math.max(8, Math.round(radius * 1.6));
  // Halloween skin swaps every ~5th sparkle for a kawaii pumpkin and
  // the remaining bright ones for gold stars — matches the seasonal
  // "pumpkins riding the comet trail" reference art.
  const isHalloween = skin && skin.id === "halloween-comet";
  for (let i = 0; i < sparkleCount; i++) {
    const t = (i / sparkleCount + phase) % 1; // along-tail position 0..1
    const px = t * tailLen * 0.95;
    // Gaussian-ish lateral spread that grows with `t` (wider at the back)
    const spread = (1 - Math.exp(-t * 2.5)) * tailHalf * 1.5;
    const py = (_rand(i * 17.31 + Math.floor(phase * 60)) - 0.5) * spread * 2;
    const sr = (0.4 + _rand(i * 41.7) * 0.9) * (radius / 7);
    // Color shifts cyan → violet → magenta → white along the tail.
    const color = t < 0.25 ? primary : t < 0.55 ? midHex : t < 0.85 ? deepHex : "#ffffff";
    if (isHalloween && i % 5 === 0 && t > 0.15 && t < 0.85) {
      // Kawaii pumpkin — orange dome + green stem + two dot eyes + smile
      const pr = sr * 3.6;
      // body
      ctx.fillStyle = hexWithAlpha("#ff8a00", (1 - t) * 0.95);
      ctx.beginPath();
      ctx.arc(px, py, pr, 0, Math.PI * 2);
      ctx.fill();
      // brighter cheek highlight
      ctx.fillStyle = hexWithAlpha("#ffcc66", (1 - t) * 0.7);
      ctx.beginPath();
      ctx.arc(px - pr * 0.35, py - pr * 0.2, pr * 0.55, 0, Math.PI * 2);
      ctx.fill();
      // stem
      ctx.fillStyle = hexWithAlpha("#3d8033", (1 - t) * 0.9);
      ctx.fillRect(px - pr * 0.18, py - pr * 1.05, pr * 0.36, pr * 0.35);
      // eyes
      ctx.fillStyle = hexWithAlpha("#1a0008", (1 - t) * 0.95);
      ctx.beginPath();
      ctx.arc(px - pr * 0.32, py + pr * 0.05, pr * 0.12, 0, Math.PI * 2);
      ctx.arc(px + pr * 0.32, py + pr * 0.05, pr * 0.12, 0, Math.PI * 2);
      ctx.fill();
      // smile
      ctx.strokeStyle = hexWithAlpha("#1a0008", (1 - t) * 0.95);
      ctx.lineWidth = Math.max(0.7, pr * 0.08);
      ctx.beginPath();
      ctx.arc(px, py + pr * 0.25, pr * 0.28, 0.15 * Math.PI, 0.85 * Math.PI);
      ctx.stroke();
    } else if (isHalloween && i % 3 === 1) {
      // 5-point gold star
      const starR = sr * 2.4;
      ctx.fillStyle = hexWithAlpha("#ffd700", (1 - t) * 0.9);
      ctx.beginPath();
      for (let k = 0; k < 10; k++) {
        const rr = k % 2 === 0 ? starR : starR * 0.42;
        const a = (Math.PI * 2 * k) / 10 - Math.PI / 2;
        const sx = px + Math.cos(a) * rr;
        const sy = py + Math.sin(a) * rr;
        if (k === 0) ctx.moveTo(sx, sy); else ctx.lineTo(sx, sy);
      }
      ctx.closePath();
      ctx.fill();
    } else {
      ctx.fillStyle = hexWithAlpha(color, (1 - t) * 0.95);
      ctx.beginPath();
      ctx.arc(px, py, sr, 0, Math.PI * 2);
      ctx.fill();
    }
    // Cross-shaped twinkle on the brightest few sparkles — sells the
    // "starburst" effect from the reference art.
    if (!isHalloween && sr > radius / 7 * 1.0 && t < 0.4) {
      ctx.strokeStyle = hexWithAlpha("#ffffff", (1 - t) * 0.85);
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(px - sr * 2.5, py); ctx.lineTo(px + sr * 2.5, py);
      ctx.moveTo(px, py - sr * 2.5); ctx.lineTo(px, py + sr * 2.5);
      ctx.stroke();
    }
  }
  ctx.restore();

  // --- Motion-history dots (ball trail; also additive) --------------------
  if (trail && trail.length > 1) {
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (let i = 0; i < trail.length; i++) {
      const t = i / trail.length;
      const p = trail[i];
      ctx.globalAlpha = 0.1 + t * 0.45;
      ctx.shadowColor = primary;
      ctx.shadowBlur = 16 * t;
      ctx.fillStyle = i > trail.length * 0.7 ? core : primary;
      ctx.beginPath();
      ctx.arc(p.x, p.y, radius * (0.25 + t * 0.6), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  // --- Nucleus ------------------------------------------------------------
  // Soft outer atmosphere, then bright white-hot core with a pin specular.
  ctx.save();
  // Outer atmospheric bloom
  ctx.globalCompositeOperation = "lighter";
  const atmo = ctx.createRadialGradient(x, y, radius * 0.2, x, y, radius * 3);
  atmo.addColorStop(0, hexWithAlpha(core, 0.55));
  atmo.addColorStop(0.5, hexWithAlpha(primary, 0.25));
  atmo.addColorStop(1, hexWithAlpha(primary, 0));
  ctx.fillStyle = atmo;
  ctx.beginPath();
  ctx.arc(x, y, radius * 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Solid core on top
  ctx.save();
  ctx.shadowColor = "#ffffff";
  ctx.shadowBlur = 30 * glowBoost;
  const coreGrad = ctx.createRadialGradient(x, y, 0.5, x, y, radius * 1.15);
  coreGrad.addColorStop(0, "#ffffff");
  coreGrad.addColorStop(0.35, "#ffffff");
  coreGrad.addColorStop(0.6, core);
  coreGrad.addColorStop(0.85, primary);
  coreGrad.addColorStop(1, hexWithAlpha(midHex, 0.55));
  ctx.fillStyle = coreGrad;
  ctx.beginPath();
  ctx.arc(x, y, radius * 1.05, 0, Math.PI * 2);
  ctx.fill();
  // Pin-point specular highlight
  ctx.shadowBlur = 0;
  ctx.fillStyle = "rgba(255,255,255,0.95)";
  ctx.beginPath();
  ctx.arc(x - radius * 0.22, y - radius * 0.28, radius * 0.25, 0, Math.PI * 2);
  ctx.fill();
  // 4-point starburst ON TOP of the nucleus — the signature "comet head"
  // sparkle from the reference art.
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  ctx.translate(x, y);
  const burstR = radius * 2.3;
  const burstG = ctx.createLinearGradient(-burstR, 0, burstR, 0);
  burstG.addColorStop(0,   "rgba(255,255,255,0)");
  burstG.addColorStop(0.5, "rgba(255,255,255,0.85)");
  burstG.addColorStop(1,   "rgba(255,255,255,0)");
  ctx.fillStyle = burstG;
  ctx.fillRect(-burstR, -radius * 0.18, burstR * 2, radius * 0.36);
  const burstG2 = ctx.createLinearGradient(0, -burstR, 0, burstR);
  burstG2.addColorStop(0,   "rgba(255,255,255,0)");
  burstG2.addColorStop(0.5, "rgba(255,255,255,0.85)");
  burstG2.addColorStop(1,   "rgba(255,255,255,0)");
  ctx.fillStyle = burstG2;
  ctx.fillRect(-radius * 0.18, -burstR, radius * 0.36, burstR * 2);
  ctx.restore();
  ctx.restore();
}

// Minimal hex -> rgba helper. Accepts #rrggbb or #rgb; falls back to white.
function hexWithAlpha(hex, alpha) {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#")) {
    return `rgba(255,255,255,${alpha})`;
  }
  let h = hex.slice(1);
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return `rgba(255,255,255,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
